import { datadogRum as datadogRumOrigin } from '@datadog/browser-rum';
import ClarityOrigin from '@microsoft/clarity';
import { gql } from '@apollo/client';
import isSSR from '../../utils/isSSR';
import { getProjectTarget, select } from '../../constants/projectTarget';
import { omitDeep } from '../../utils/omitdeep';
import { redactEbsVideoUrl } from '../../ebs-video/playback';

const datadogRum = datadogRumOrigin;
const Clarity = ClarityOrigin;

const hasDatadogEnv =
  process.env.NEXT_PUBLIC_DATA_DOG_APPLICATION_ID && process.env.NEXT_PUBLIC_DATA_DOG_CLIENT_TOKEN;

const hasClarityEnv = process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID;

type InitAPMInput = {
  version: string;
  serviceName: string;
  env: string;
  sessionReplayRecording?: boolean;
};

export const initAPM = ({ env, version, serviceName, sessionReplayRecording = false }: InitAPMInput) => {
  if (isSSR) {
    return;
  }
  if (hasDatadogEnv) {
    // datadog
    datadogRum.init({
      applicationId: process.env.NEXT_PUBLIC_DATA_DOG_APPLICATION_ID as string,
      clientToken: process.env.NEXT_PUBLIC_DATA_DOG_CLIENT_TOKEN as string,
      site: 'datadoghq.com',
      sampleRate: 100,
      premiumSampleRate: 100,
      trackInteractions: true,
      allowedTracingOrigins: [
        /https:\/\/.*\.english\.management/,
        /https:\/\/.*\gwstage.english\.management/,
        ...(process.env.NEXT_PUBLIC_GRAPHQL_URL ? [new URL(process.env.NEXT_PUBLIC_GRAPHQL_URL).origin] : []),
      ],
      service:
        serviceName +
        select({
          hub: '',
          mildang: '',
          schoolpt: getProjectTarget(),
        }),
      version: version,
      env: env,
      beforeSend: (event, context) => {
        // ResizeObserver loop 에러는 무해한 브라우저 노이즈 — Datadog에서 제외
        if (event.type === 'error' && event.error.message.includes('ResizeObserver loop')) {
          return false;
        }

        // media 성능 이벤트와 네트워크 오류에 EBS 서명 query가 저장되지 않도록 한다.
        if (event.type === 'resource') {
          event.resource.url = redactEbsVideoUrl(event.resource.url);
        } else if (event.type === 'error' && event.error.resource) {
          const originalUrl = event.error.resource.url;
          const redactedUrl = redactEbsVideoUrl(originalUrl);
          if (originalUrl !== redactedUrl) {
            event.error.resource.url = redactedUrl;
            event.error.message = event.error.message.split(originalUrl).join(redactedUrl);
            if (event.error.stack) {
              event.error.stack = event.error.stack.split(originalUrl).join(redactedUrl);
            }
          }
        }

        // collect a RUM resource's response headers
        if (
          event.type === 'resource' &&
          event.resource.type === 'fetch' &&
          (event.resource.url.endsWith('graphql') || event.resource.url === 'https://api.ai-dt.org/')
        ) {
          try {
            const { operationName, variables, query } = JSON.parse((context as any).requestInit.body);

            event.context = {
              ...event.context,
              'apollo.client.variablesStr': JSON.stringify(omitDeep(variables, ['password'])),
              'apollo.client.operationName': operationName,
              'apollo.client.query': query,
            };
          } catch (e) {}
        }
        return true;
      },
      ...(sessionReplayRecording && {
        defaultPrivacyLevel: 'mask-user-input',
      }),
    });

    if (sessionReplayRecording) {
      // TODO: 부하 테스트로 잠시 주석 처리
      datadogRum.startSessionReplayRecording();
    }
  }

  if (hasClarityEnv) {
    // clarity
    Clarity.init(process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID as string);
  }
};

export const setGlobalContextProperty = (key: string, property: any) => {
  if (!hasDatadogEnv) {
    return;
  }
  datadogRum.setGlobalContextProperty(key, property);
};

export const removeUser = () => {
  if (!hasDatadogEnv) {
    return;
  }
  datadogRum.clearUser();
};

export const addAction = (name: string, context?: object | undefined) => {
  if (!hasDatadogEnv) {
    return;
  }
  datadogRum.addAction(name, context);
};

export const pageview = (_name: string) => {
  if (!hasDatadogEnv) {
    return;
  }
  // datadogRum.startView(name);
};

export const addError = (error: unknown, context?: object | undefined) => {
  if (!hasDatadogEnv) {
    return;
  }
  datadogRum.addError(error, context);
};

gql`
  fragment Datadog_SetUser_User on User {
    id
    email
    name
  }
`;
